import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchDashboard,
  fetchLive,
  dashboardSelector,
  liveSelector,
  registerAlias,
} from "./dashboardSlice";
import Alert from "react-bootstrap/Alert";
import { Line, Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import DatePicker from "react-datepicker";
import { parse, format } from "date-fns";
import fr from "date-fns/locale/fr";
import styles from "./Dashboard.module.css";
import Spinner from "react-bootstrap/Spinner";
import { DateRangePicker } from "rsuite";
import Message from "rsuite/Message";
import toaster from "rsuite/toaster";
import LiveTable from "./LiveTable";
import Cookies from "js-cookie";
import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axios from "axios";
import { API_URL } from "../../Constants";

export function Dashboard() {
  const ref = useRef();
  const dispatch = useDispatch();
  const { dashboard, loading, hasErrors } = useSelector(dashboardSelector);
  const live = useSelector(liveSelector);
  const [showTable, setShowTable] = useState(Cookies.get("showTable") === "true");

  // États pour la gestion de l'alias
  const [showAliasPopup, setShowAliasPopup] = useState(false);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const userAlias = useSelector((state) => state.connexion.utilisateur.alias);
  
  // Vérifier dès le montage si le cookie "Alias" existe
  useEffect(() => {
    if (!Cookies.get("Alias") && (!userAlias || userAlias === "null" || userAlias === "[null]" || userAlias.trim() === "")) {
      setShowAliasPopup(true);
    }
  }, [userAlias]);

  // --- Code existant du Dashboard (gestion des dates, rafraîchissement, etc.) ---
  var date = new Date();
  var d_min = new Date(date.getFullYear(), date.getMonth(), 1);
  if (date === d_min) {
    d_min.setDate(d_min.getDate() - 1);
  }
  const [date_max, set_max_date] = useState(date);
  const [date_min, set_min_date] = useState(d_min);
  const [dateRange, setDateRange] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [liveLoaded, setLiveLoaded] = useState(false);
  const restaurant_choisi = useSelector((state) => state.connexion.restaurant_choisi);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(120);

  const refreshTable = async () => {
    try {
      await dispatch(fetchLive("hebdomadaire"));
      setLiveLoaded(true);
    } catch (error) {
      console.error("Erreur lors du rafraîchissement du tableau :", error);
    }
  };

  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
      setTimeUntilRefresh((prev) => {
        if (prev > 1) {
          return prev - 1;
        } else {
          refreshTable();
          return 120;
        }
      });
    }, 1000);

    refreshTable();

    return () => {
      clearInterval(clockInterval);
    };
  }, []);

  useEffect(() => {
    refreshTable();
  }, [restaurant_choisi]);

  useEffect(() => {
    dispatch(fetchDashboard("hebdomadaire")).then(() => setHasLoaded(true));
  }, [restaurant_choisi]);

  useEffect(() => {
    dispatch(fetchLive("hebdomadaire")).then(() => setLiveLoaded(true));
  }, [restaurant_choisi]);

  const calculateDaysDifference = (start, end) => {
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.round(Math.abs((end - start) / oneDay));
  };

  const toggleTableVisibility = () => {
    const newShowTable = !showTable;
    setShowTable(newShowTable);
    Cookies.set("showTable", newShowTable, { expires: 30 });
  };

  const handleDateChange = (range) => {
    if (range[0] && range[1]) {
      const daysDifference = calculateDaysDifference(range[0], range[1]);
      if (daysDifference > 45) {
        const message = (
          <Message showIcon type="warning">
            L'intervalle de dates ne peut pas dépasser 45 jours.
          </Message>
        );
        toaster.push(message, "topEnd");
      } else {
        setDateRange(range);
      }
    } else {
      setDateRange(range);
    }
  };

  const imprEdit = live?.etatDuService;

  // --- Fin du code existant du Dashboard ---

  // Fonctions pour gérer la demande d'alias
  const handleAliasYes = () => {
    setShowAliasPopup(false);
    setShowAliasModal(true);
  };

  const handleAliasNo = () => {
    if (window.confirm("Êtes-vous sûr de vouloir refuser la création d'un Alias ?")) {
      Cookies.set("Alias", "refused", { expires: 365 });
      setShowAliasPopup(false);
    }
  };

  const handleAliasLater = () => {
    if (
      window.confirm(
        "Confirmez le choix 'Plus tard'. L'alias vous sera redemandé à la prochaine connexion."
      )
    ) {
      Cookies.set("Alias", "later");
      setShowAliasPopup(false);
    }
  };

  // Fonction de validation de l'alias avec appel API et vérification regex côté front
  const handleAliasModalValidate = async () => {
    const trimmedAlias = aliasInput.trim();
    const regex = /^.{6,}$/;
    if (!regex.test(trimmedAlias)) {
      alert("L'alias doit contenir au moins 6 caractères.");
      return;
    }
    try {
      await dispatch(registerAlias(trimmedAlias)).unwrap();
      Cookies.set("Alias", trimmedAlias, { expires: 365 });
      setShowAliasModal(false);
      alert("Alias enregistré avec succès.");
    } catch (error) {
      alert("Erreur lors de la communication avec le serveur.");
    }
  };

  return (
    <div>
      {/* Popup initial pour demander la création d'un Alias */}
      {showAliasPopup && (
        <Modal
          show
          onHide={() => setShowAliasPopup(false)}
          className={styles.customModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Création d'Alias</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Voulez-vous créer un alias ?</p>
          </Modal.Body>
          <Modal.Footer>
            <Button className={styles.buttonValidate} onClick={handleAliasYes}>
              Oui
            </Button>
            <Button variant="secondary" onClick={handleAliasNo}>
              Non
            </Button>
            <Button variant="warning" onClick={handleAliasLater}>
              Plus tard
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Modal pour saisir l'alias */}
      {showAliasModal && (
        <Modal
          show
          onHide={() => setShowAliasModal(false)}
          className={styles.customModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Choisissez votre Alias</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group controlId="aliasInput">
                <Form.Label>Alias</Form.Label>
                <Form.Control
                  type="text"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  placeholder="Entrez votre alias (6 caractères min)"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button  className={styles.buttonValidate} onClick={handleAliasModalValidate}>
              Valider
            </Button>
            <Button variant="secondary" onClick={() => setShowAliasModal(false)}>
              Annuler
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Rendu habituel du dashboard */}
      {hasLoaded ? (
        <div>
          <div className={styles.switch}>
            <ButtonGroup as="div" aria-label="Basic example">
              <Button
                as="div"
                variant="secondary"
                className={styles.bouton_dashboard}
                onClick={() => dispatch(fetchDashboard("hebdomadaire"))}
              >
                Hebdomadaire
              </Button>
              <Button
                as="div"
                variant="secondary"
                className={styles.bouton_dashboard}
                onClick={() => dispatch(fetchDashboard("mensuel"))}
              >
                Mois en cours
              </Button>
            </ButtonGroup>
            <div className={styles.div_date}>
              <div className={styles.date}>Personnaliser :</div>
              <DateRangePicker
                showOneCalendar={true}
                locale={fr}
                format="dd/MM/yyyy"
                placeholder="Choisir un intervalle"
                isoWeek={true}
                onChange={handleDateChange}
              />
              <div className={styles.date}>
                <Button
                  type="submit"
                  variant="outline-danger"
                  className={styles.button}
                  onClick={() => {
                    if (dateRange[0] > dateRange[1]) {
                      const message = (
                        <Message showIcon type="warning">
                          La date de début doit être antérieure à la date de fin.
                        </Message>
                      );
                      toaster.push(message, "topEnd");
                    } else if (dateRange[1] > new Date()) {
                      const message = (
                        <Message showIcon type="warning">
                          Impossible d'afficher une date dans le futur !
                        </Message>
                      );
                      toaster.push(message, { placement: "topEnd" });
                    } else {
                      dispatch(
                        fetchDashboard(
                          "personnaliser",
                          format(dateRange[0], "yyyyMMdd"),
                          format(dateRange[1], "yyyyMMdd")
                        )
                      );
                    }
                  }}
                >
                  Valider
                </Button>
              </div>
            </div>
          </div>
          {imprEdit === null ? null : imprEdit === 0 ? (
            <Card className={styles.dashboardCard}>
              <Card.Header className={styles.cardHeader}>
                <h2 className={styles.titleLive}>Tableau Live</h2>
                <Button
                  variant={showTable ? "secondary" : "danger"}
                  className={`${styles.toggleButton} ${
                    showTable ? styles.hideButton : styles.showButton
                  }`}
                  onClick={toggleTableVisibility}
                >
                  {showTable ? "Cacher" : "Afficher"}
                </Button>
              </Card.Header>
              {showTable && (
                <div className={`card-body ${styles.customCardBody}`}>
                  {liveLoaded ? (
                    <LiveTable
                      live={live}
                      onRefresh={refreshTable}
                      dateServiceEnDirectLong={
                        live?.dates?.serviceEnCours
                          ? `Service du ${format(
                              parse(live.dates.serviceEnCours, "dd/MM/yyyy", new Date()),
                              "EEEE dd MMMM yyyy",
                              { locale: fr }
                            )} en direct`
                          : "Service en direct"
                      }
                      dateServiceEnDirectCourt={
                        live?.dates?.serviceEnCours
                          ? `Service du ${format(
                              parse(live.dates.serviceEnCours, "dd/MM/yyyy", new Date()),
                              "eee dd MMM",
                              { locale: fr }
                            ).replace(".", "")} en direct`
                          : "Service en direct"
                      }
                      dateDernierServiceAJour={
                        live?.dates?.serviceEnCours
                          ? `Dernière mise à jour : ${live.dates.finServiceEnCours} à ${live.dates.heureFinServiceEnCours}`
                          : "Pas de dernière mise à jour"
                      }
                    />
                  ) : (
                    <Alert variant="secondary">
                      Chargement du tableau <Spinner animation="border" />
                    </Alert>
                  )}
                </div>
              )}
            </Card>
          ) : (
            <Card className={`${styles.dashboardCard} ${styles.noServiceCard}`}>
              <Card.Body>
                <h4>
                  Service du{" "}
                  {live?.dates?.serviceEnCours
                    ? format(
                        parse(live.dates.serviceEnCours, "dd/MM/yyyy", new Date()),
                        "EEEE dd MMMM yyyy",
                        { locale: fr }
                      )
                    : "(erreur date de service non fournie)"}{" "}
                  clôturé le{" "}
                  {live?.dates?.serviceEnCours
                    ? `${live.dates.finServiceEnCours} à ${live.dates.heureFinServiceEnCours}`
                    : "(erreur date et heure pour fin de service non fournies)"}
                  .
                </h4>
              </Card.Body>
            </Card>
          )}
          <div className={styles.grille}>
            <article className={styles.graphique}>
              <h2>Chiffre d'affaires</h2>
              <Line
                ref={ref}
                data={{
                  labels: [...dashboard[0]],
                  datasets: [
                    {
                      label: "CA Restaurant avec taxes",
                      lineTension: 0,
                      data: [...dashboard[1]],
                      fill: false,
                      backgroundColor: "rgb(244,0,32)",
                      borderColor: "rgb(247,76,98)",
                    },
                    {
                      label: "CA Brasserie avec taxes",
                      lineTension: 0,
                      data: [...dashboard[2]],
                      fill: false,
                      backgroundColor: "rgb(10,117,173)",
                      borderColor: "rgb(83,158,197)",
                    },
                    {
                      label: "Total avec taxes",
                      lineTension: 0,
                      data: [...dashboard[3]],
                      fill: false,
                      backgroundColor: "rgb(139,125,123)",
                      borderColor: "rgb(205,183,181)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: Math.ceil((Math.max(...dashboard[1], ...dashboard[2], ...dashboard[3]) + 500) / 100) * 100,
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom",
                    },
                    datalabels: {
                      clamp: true,
                      anchor: "center",
                      color: "black",
                      align: "top",
                      font: {
                        weight: "bold",
                        size: 12,
                      },
                    },
                  },
                }}
                plugins={[ChartDataLabels]}
              />
            </article>
            <article className={styles.graphique}>
              <h2>Nombre de couverts et tickets</h2>
              <Bar
                ref={ref}
                data={{
                  labels: [...dashboard[0]],
                  datasets: [
                    {
                      label: "Nombre de couverts",
                      lineTension: 0,
                      data: [...dashboard[4]],
                      fill: false,
                      backgroundColor: "rgb(244,0,32)",
                      borderColor: "rgb(247,76,98)",
                    },
                    {
                      label: "Nombre de tickets bar",
                      lineTension: 0,
                      data: [...dashboard[9]],
                      fill: false,
                      backgroundColor: "rgb(10,117,173)",
                      borderColor: "rgb(83,158,197)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: Math.ceil((Math.max(...dashboard[4], ...dashboard[9]) + 50) / 50) * 50,
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom",
                    },
                    datalabels: {
                      color: "black",
                      align: "mid",
                      font: {
                        weight: "bold",
                        size: 12,
                      },
                    },
                  },
                }}
                plugins={[ChartDataLabels]}
              />
            </article>
            <article className={styles.graphique}>
              <h2>Pourcentage d'annulations</h2>
              <Line
                ref={ref}
                data={{
                  labels: [...dashboard[0]],
                  datasets: [
                    {
                      label: "Pourcentage d'annulations",
                      lineTension: 0,
                      data: [...dashboard[5]],
                      fill: false,
                      backgroundColor: "rgb(244,0,32)",
                      borderColor: "rgb(247,76,98)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      min: 0,
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom",
                    },
                    datalabels: {
                      color: "black",
                      align: "top",
                      font: {
                        weight: "bold",
                        size: 12,
                      },
                    },
                  },
                }}
                plugins={[ChartDataLabels]}
              />
            </article>
            <article className={styles.graphique}>
              <h2>Groupe de famille</h2>
              <Bar
                ref={ref}
                data={{
                  labels: [...dashboard[7]],
                  datasets: [
                    {
                      label: "CA H.T",
                      lineTension: 0,
                      data: [...dashboard[8]],
                      fill: false,
                      backgroundColor: "rgb(244,0,32)",
                      borderColor: "rgb(247,76,98)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: Math.ceil((Math.max(...dashboard[8]) + 50) / 50) * 50,
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom",
                    },
                    datalabels: {
                      color: "black",
                      align: "mid",
                      font: {
                        weight: "bold",
                        size: 12,
                      },
                    },
                  },
                }}
                plugins={[ChartDataLabels]}
              />
            </article>
          </div>
        </div>
      ) : (
        <Alert variant="secondary">
          Chargement du dashboard <Spinner styles={styles.spinner} animation="border" />
        </Alert>
      )}
    </div>
  );
}
